import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import contractUtils, { 
  getContract, 
  getCaseDetails, 
  connectWallet, 
  registerCase,
  formatEthAmount,
  formatAddress,
  getExplorerUrl
} from '../utils/contractUtils';
import ipfsUtils from '../utils/ipfsUtils';
import './CivilClaimsRegistry.css';

/**
 * CivilClaimsRegistry Component
 * 
 * This component provides UI to interact with the CivilClaimsCaseRegistry contract.
 * Features include:
 * - Wallet connection
 * - Case registration
 * - Case lookup
 */
const CivilClaimsRegistry = () => {
  // State variables
  const [account, setAccount] = useState('');
  const [networkName, setNetworkName] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const [filingFee, setFilingFee] = useState('0.002');
  // Removed unused verificationFee state
  const [isRegistrar, setIsRegistrar] = useState(false);
  const [isCourtAdmin, setIsCourtAdmin] = useState(false);
  
  // Case registration form state
  const [initialStatus, setInitialStatus] = useState('Filed');
  const [plaintiffAddress, setPlaintiffAddress] = useState('');
  const [defendantAddress, setDefendantAddress] = useState('');
  const [caseDescription, setCaseDescription] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isUploadingToIpfs, setIsUploadingToIpfs] = useState(false);
  const [registrationTx, setRegistrationTx] = useState(null);
  const [caseId, setCaseId] = useState(null);
  
  // Case lookup state
  const [lookupCaseId, setLookupCaseId] = useState('');
  const [caseDetails, setCaseDetails] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Connect to wallet
  const handleConnect = async () => {
    setIsConnecting(true);
    setError('');
    
    try {
      const walletData = await connectWallet();
      const userAddress = walletData.address;
      setAccount(userAddress);
      
      // Network information is already available from connectWallet
      setNetworkName(contractUtils.getNetworkName(walletData.chainId));
      
      // No need to switch networks, connectWallet already does this
      
      // Get contract fees
      const contract = await getContract();
      const fileFee = await contract.filingFee();
      
      setFilingFee(formatEthAmount(fileFee));
      
      // Set plaintiff address to connected wallet
      setPlaintiffAddress(userAddress);
      
      // Check user roles
      await checkUserRoles(userAddress);
      
    } catch (err) {
      console.error("Connection error:", err);
      setError(err.message || "Failed to connect wallet");
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Check user roles in the contract
  const checkUserRoles = async (address) => {
    try {
      const contract = await getContract();
      
      const registrarRole = await contract.REGISTRAR_ROLE();
      const courtAdminRole = await contract.COURT_ADMIN_ROLE();
      
      const hasRegistrarRole = await contract.hasRole(registrarRole, address);
      const hasCourtAdminRole = await contract.hasRole(courtAdminRole, address);
      
      setIsRegistrar(hasRegistrarRole);
      setIsCourtAdmin(hasCourtAdminRole);
    } catch (err) {
      console.error("Error checking roles:", err);
    }
  };
  
  // Handle case registration
  const handleRegisterCase = async (e) => {
    e.preventDefault();
    setError('');
    setIsRegistering(true);
    setRegistrationTx(null);
    setCaseId(null);
    
    try {
      // Validate inputs
      if (!plaintiffAddress) {
        throw new Error("Plaintiff address is required");
      }
      
      if (!ethers.isAddress(plaintiffAddress)) {
        throw new Error("Invalid plaintiff address");
      }
      
      if (defendantAddress && !ethers.isAddress(defendantAddress)) {
        throw new Error("Invalid defendant address");
      }
      
      if (!initialStatus) {
        throw new Error("Case status is required");
      }
      
          // Create metadata for IPFS
      const metadataForIpfs = {
        description: caseDescription,
        claimAmount: claimAmount,
        timestamp: new Date().toISOString(),
        status: initialStatus
      };
      
      // Upload to IPFS using our ipfsUtils
      setIsUploadingToIpfs(true);
      let ipfsHash;
      try {
        const ipfsCid = await ipfsUtils.uploadToIPFS(metadataForIpfs);
        ipfsHash = `ipfs://${ipfsCid}`;
        setIsUploadingToIpfs(false);
      } catch (err) {
        setIsUploadingToIpfs(false);
        throw new Error(`IPFS upload failed: ${err.message}`);
      }
      
      // Register the case on the blockchain
      const result = await registerCase(
        ipfsHash,
        initialStatus,
        plaintiffAddress,
        defendantAddress || ethers.ZeroAddress
      );
      
      setRegistrationTx(result.receipt.hash);
      setCaseId(result.tokenId);
      
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to register case");
    } finally {
      setIsRegistering(false);
    }
  };
  
  // Handle case lookup
  const handleLookupCase = async (e) => {
    e.preventDefault();
    setLookupError('');
    setCaseDetails(null);
    setIsLookingUp(true);
    
    try {
      if (!lookupCaseId.trim()) {
        throw new Error("Please enter a case ID");
      }
      
      const caseId = parseInt(lookupCaseId.trim());
      if (isNaN(caseId)) {
        throw new Error("Case ID must be a number");
      }
      
      // Get case details from the contract
      const details = await getCaseDetails(caseId);
      setCaseDetails(details);
      
    } catch (err) {
      console.error("Lookup error:", err);
      setLookupError(err.message || "Failed to look up case");
    } finally {
      setIsLookingUp(false);
    }
  };
  
  // Clear registration form
  const handleClearForm = () => {
    setInitialStatus('Filed');
    setDefendantAddress('');
    setCaseDescription('');
    setClaimAmount('');
    setRegistrationTx(null);
    setCaseId(null);
    setError('');
  };
  
  // Set up event listeners for wallet changes
  useEffect(() => {
    const setupListeners = async () => {
      if (window.ethereum) {
        window.ethereum.on('accountsChanged', (accounts) => {
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            setPlaintiffAddress(accounts[0]);
            checkUserRoles(accounts[0]);
          } else {
            setAccount('');
            setIsRegistrar(false);
            setIsCourtAdmin(false);
          }
        });
        
        window.ethereum.on('chainChanged', () => {
          window.location.reload();
        });
      }
    };
    
    setupListeners();
    
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', () => {});
        window.ethereum.removeListener('chainChanged', () => {});
      }
    };
  }, []);

  return (
    <div className="civil-claims-registry">
      <h2>Civil Claims Registry</h2>
      
      {/* Wallet Connection Section */}
      <div className="card">
        <h3>Wallet Connection</h3>
        {!account ? (
          <button 
            className="btn btn-primary" 
            onClick={handleConnect}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting...' : 'Connect Wallet'}
          </button>
        ) : (
          <div className="wallet-info">
            <p><strong>Connected Account:</strong> {formatAddress(account)}</p>
            <p><strong>Network:</strong> {networkName}</p>
            <p><strong>Roles:</strong> 
              {isRegistrar && <span className="badge">Registrar</span>}
              {isCourtAdmin && <span className="badge">Court Admin</span>}
              {!isRegistrar && !isCourtAdmin && <span>None</span>}
            </p>
          </div>
        )}
        {error && <div className="error">{error}</div>}
      </div>
      
      {/* Case Registration Section */}
      {account && (
        <div className="card">
          <h3>Register New Case</h3>
          <form onSubmit={handleRegisterCase}>
            <div className="form-group">
              <label>Initial Status:</label>
              <select 
                value={initialStatus}
                onChange={(e) => setInitialStatus(e.target.value)}
                required
              >
                <option value="Filed">Filed</option>
                <option value="Pending">Pending</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Contested">Contested</option>
                <option value="In Progress">In Progress</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Plaintiff Address:</label>
              <input 
                type="text"
                value={plaintiffAddress}
                onChange={(e) => setPlaintiffAddress(e.target.value)}
                placeholder="0x..."
                required
              />
            </div>
            
            <div className="form-group">
              <label>Defendant Address (Optional):</label>
              <input 
                type="text"
                value={defendantAddress}
                onChange={(e) => setDefendantAddress(e.target.value)}
                placeholder="0x..."
              />
            </div>
            
            <div className="form-group">
              <label>Case Description:</label>
              <textarea 
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                placeholder="Enter case description"
                rows="4"
              />
            </div>
            
            <div className="form-group">
              <label>Claim Amount (ETH):</label>
              <input 
                type="text"
                value={claimAmount}
                onChange={(e) => setClaimAmount(e.target.value)}
                placeholder="0.0"
              />
            </div>
            
            <div className="fee-info">
              <p>Filing Fee: {filingFee} ETH</p>
            </div>
            
            <div className="action-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isRegistering || isUploadingToIpfs || !isRegistrar}
              >
                {isUploadingToIpfs ? 'Uploading to IPFS...' : 
                 isRegistering ? 'Registering Case...' : 
                 'Register Case'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleClearForm}
              >
                Clear Form
              </button>
            </div>
            
            {!isRegistrar && (
              <div className="warning">
                You need the Registrar role to register cases
              </div>
            )}
          </form>
          
          {error && <div className="error">{error}</div>}
          
          {registrationTx && (
            <div className="success-message">
              <h4>Case Registered Successfully!</h4>
              <p>Transaction: <a href={getExplorerUrl(registrationTx)} target="_blank" rel="noopener noreferrer">
                {formatAddress(registrationTx)}
              </a></p>
              <p>Case ID: {caseId}</p>
              <p className="text-sm mt-2">
                <span className="font-medium">Note:</span> Case metadata is stored securely on IPFS using the Infura gateway 
                at <code>https://freeweb3.infura-ipfs.io/ipfs/...</code>
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Case Lookup Section */}
      {account && (
        <div className="card">
          <h3>Look Up Case</h3>
          <form onSubmit={handleLookupCase}>
            <div className="form-group">
              <label>Case ID:</label>
              <input 
                type="text"
                value={lookupCaseId}
                onChange={(e) => setLookupCaseId(e.target.value)}
                placeholder="Enter case ID"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLookingUp}
            >
              {isLookingUp ? 'Looking Up...' : 'Look Up Case'}
            </button>
          </form>
          
          {lookupError && <div className="error">{lookupError}</div>}
          
          {caseDetails && (
            <div className="case-details">
              <h4>Case #{caseDetails.tokenId} Details</h4>
              <table>
                <tbody>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>{caseDetails.status}</td>
                  </tr>
                  <tr>
                    <td><strong>Plaintiff:</strong></td>
                    <td>
                      <a href={`${contractUtils.BASE_MAINNET.blockExplorer}/address/${caseDetails.plaintiff}`} target="_blank" rel="noopener noreferrer">
                        {formatAddress(caseDetails.plaintiff)}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Defendant:</strong></td>
                    <td>
                      {caseDetails.defendant === ethers.ZeroAddress 
                        ? 'Not Assigned' 
                        : (
                          <a href={`${contractUtils.BASE_MAINNET.blockExplorer}/address/${caseDetails.defendant}`} target="_blank" rel="noopener noreferrer">
                            {formatAddress(caseDetails.defendant)}
                          </a>
                        )
                      }
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Metadata URI:</strong></td>
                    <td>
                      {caseDetails.uri && caseDetails.uri.startsWith('ipfs://') ? (
                        <a 
                          href={ipfsUtils.getIPFSGatewayUrl(caseDetails.uri.replace('ipfs://', ''))}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {caseDetails.uri}
                        </a>
                      ) : (
                        caseDetails.uri
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* CSS styles imported from SmallClaimsRegistry.css */}
    </div>
  );
};

export default CivilClaimsRegistry;